import axios from 'axios';
import { READINESS_CODES as FRIENDLY_CODES } from './friendlyCodes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - add auth token + device id (Day 9 device governance)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const deviceId = localStorage.getItem('gc360_device_id');
  if (deviceId) {
    config.headers['X-Device-Id'] = deviceId;
  }
  return config;
});

// Single-flight refresh: when the access token expires, an SPA fires many
// requests that all 401 at once. Without coordination each would call
// /auth/refresh, and refresh-token rotation would blacklist the shared token
// so all-but-one refresh fails → premature forced logout. We serialise them
// through one in-flight refresh promise so only ONE rotation happens and every
// queued request retries with the freshly minted access token.
let refreshPromise = null;

async function runTokenRefresh() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) throw new Error('No refresh token');
  const res = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: refreshToken });
  const { access_token, refresh_token: newRefresh } = res.data;
  localStorage.setItem('access_token', access_token);
  localStorage.setItem('refresh_token', newRefresh);
  return access_token;
}

// Response interceptor - normalize paginated responses + handle 401 refresh
api.interceptors.response.use(
  (response) => {
    // Normalize: if response has { items, page, pageSize, total }, also set .data.data = items
    if (response.data && Array.isArray(response.data.items) && !response.data.data) {
      response.data.data = response.data.items;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Extract standardized error message for consumers (top-level `error` or the FastAPI `detail.error` wrapper).
    const errObj = error.response?.data?.error || error.response?.data?.detail?.error;
    if (errObj && typeof errObj === 'object') {
      error.userMessage = errObj.message || 'An error occurred';
      error.errorCode = errObj.code || 'UNKNOWN';
      error.errorDetails = errObj.details || {};
    }
    // AUTH-2: rate-limit retry information (body value authoritative; Retry-After header as fallback).
    if (error.response?.status === 429) {
      const headerRetry = Number(error.response.headers?.['retry-after']);
      const bodyRetry = Number(error.errorDetails?.retry_after_seconds);
      error.retryAfterSeconds = Number.isFinite(bodyRetry) && bodyRetry > 0 ? bodyRetry : (Number.isFinite(headerRetry) && headerRetry > 0 ? headerRetry : null);
      error.lockedUntil = error.errorDetails?.locked_until || null;
    }

    // Day-9: blocked device -> remote logout + blocked screen
    if (error.response?.status === 403 && error.response?.data?.error?.code === 'DEVICE_BLOCKED') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      if (!window.location.pathname.startsWith('/mobile/blocked')) {
        window.location.href = '/mobile/blocked';
      }
      return Promise.reject(error);
    }

    // Phase 4B-3b: auth-flow endpoints legitimately return 401 for user-input
    // errors (wrong password, wrong TOTP/recovery code, expired/locked challenge).
    // They must surface inline — never trigger the session-refresh/redirect logic.
    const reqUrl = originalRequest?.url || '';
    const isAuthFlow = reqUrl.includes('/auth/login') || reqUrl.includes('/auth/2fa/challenge');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthFlow) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          // Reuse an in-flight refresh if one is already running.
          if (!refreshPromise) {
            refreshPromise = runTokenRefresh().finally(() => { refreshPromise = null; });
          }
          const access_token = await refreshPromise;
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Extract user-friendly error message from API error.
 * Usage: catch(e) { toast.error(getErrorMessage(e)); }
 */
export function getErrorMessage(error) {
  if (error.userMessage) return error.userMessage;
  const code = error.response?.data?.error?.code || error.response?.data?.detail?.code;
  if (code && FRIENDLY_CODES[code]) return FRIENDLY_CODES[code];
  if (error.response?.data?.error?.message) return error.response.data.error.message;
  if (error.response?.data?.detail) {
    if (typeof error.response.data.detail === 'string') return error.response.data.detail;
    return error.response.data.detail.message || JSON.stringify(error.response.data.detail);
  }
  return error.message || 'An unexpected error occurred';
}

/**
 * Fetch every page of a server-paginated list endpoint and return the combined
 * array. Endpoints cap page_size at 100, so we loop until `total` is reached.
 * Lets list pages do full client-side search / filter / pagination.
 */
export async function fetchAllPages(path, params = {}) {
  const pageSize = 100;
  let page = 1;
  let all = [];
  for (;;) {
    const res = await api.get(path, { params: { ...params, page, pageSize } });
    const items = res.data?.data || res.data?.items || [];
    all = all.concat(items);
    const total = typeof res.data?.total === 'number' ? res.data.total : all.length;
    if (items.length === 0 || all.length >= total || page >= 100) break;
    page += 1;
  }
  return all;
}

export default api;
